// @ts-ignore Ignore unused commented imports
import { Injectable, inject } from '@angular/core';

import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Store, select, Action } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { EMPTY, of } from 'rxjs';
import { switchMap, startWith, finalize, map, withLatestFrom } from 'rxjs/operators';
import { CropAid } from 'src/app/crop-aid/crop-aid.model';
import { selectCropAids, selectOrganicCropAids } from 'src/app/crop-aid/store/crop-aid.selectors';
import * as LegalRestrictionActions from 'src/app/legal-restriction/store/legal-restriction.actions';
import { CopyTreatmentsDialogComponent } from 'src/app/shared/components/copy-treatments-dialog/copy-treatments-dialog.component';
import { TreatmentDialogComponent } from 'src/app/shared/components/treatment-dialog/treatment-dialog.component';
import { TreatmentSchemaDialogComponent } from 'src/app/shared/components/treatment-schema-dialog/treatment-schema-dialog.component';
import {
  AppState,
  Treatment,
  SalesOrderPeriod,
  TemplateTableItem,
  Crop,
  Template,
  TreatmentType,
  TemplateType,
} from 'src/app/shared/models';
import { ApiService } from 'src/app/shared/services/api.service';
import { SoilType } from 'src/app/soil-type/soil-type.model';
import { selectSoilTypes } from 'src/app/soil-type/store/soil-type.selectors';
import { SetTemplate } from 'src/app/store/actions';
import {
  selectCrops,
  selectAvailableCropYears,
  selectSalesOrderPeriods,
} from 'src/app/store/selectors';

import {
  TemplateActionTypes,
  SetEditingTemplate,
  GetEditingTemplate,
  EditTreatment,
  SaveTreatment,
  EditTreatmentSchema,
  SaveTreatmentSchema,
  FetchTemplate,
  CopyTreatments,
} from './actions';
import { selectEditingTemplate, selectEditingTreatments, areTreatmentsValid } from './selectors';

@Injectable()
export class TemplateEffects {
  protected store = inject<Store<AppState>>(Store);
  protected actions$ = inject(Actions);
  protected api = inject(ApiService);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);

  getEditingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.GetEditingTemplate),
      switchMap(({ id }: GetEditingTemplate) =>
        this.api.getTemplate(id).pipe(map((template) => new SetEditingTemplate(template))),
      ),
    ),
  );

  fetchTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.FetchTemplate),
      switchMap(({ id }: FetchTemplate) =>
        this.api.getTemplate(id).pipe(
          switchMap((template) => [new SetTemplate(template), new GetEditingTemplate(id)]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.AddTreatment),
      withLatestFrom(
        this.store.pipe(select(selectEditingTemplate)),
        this.store.pipe(select(areTreatmentsValid)),
      ),
      switchMap(([_, template, treatmentsValid]: [Action, Template, boolean]) =>
        treatmentsValid
          ? of(
              new EditTreatment({
                id: null,
                name: '',
                objective: template.templateType === TemplateType.GBM ? 'weed' : 'basegift',
                periodId: null,
                aidAdvices: [],
                moment: '',
                pointOfAttention: '',
                frequency: 1,
                driftReduction: null,
                treatmentType: null,
              }),
            )
          : this.dialog
              .notify({
                title: this.locale.translate('cannotAddTreatment'),
                text: this.locale.translate('invalidTreatments'),
              })
              .pipe(switchMap(() => EMPTY)),
      ),
    ),
  );

  openTreatmentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.EditTreatment),
      withLatestFrom(
        this.store.pipe(select(selectCropAids)),
        this.store.pipe(select(selectOrganicCropAids)),
        this.store.pipe(select(selectSalesOrderPeriods)),
        this.store.pipe(select(selectEditingTemplate)),
      ),
      switchMap(
        ([action, cropAids, organicCropAids, salesOrderPeriods, template]: [
          EditTreatment,
          CropAid[],
          CropAid[],
          SalesOrderPeriod[],
          Template,
        ]) => {
          // Add a matching treatment type to the treatment
          const fertilizationObjectives = [
            { value: 'basegift', name: this.locale.translate('basegift') },
            { value: 'extraFertilization', name: this.locale.translate('extraFertilization') },
            { value: 'leafFertilization', name: this.locale.translate('leafFertilization') },
            { value: 'fertigation', name: this.locale.translate('fertigation') },
          ];
          const gbmObjectives = [
            { value: 'weed', name: this.locale.translate('weed') },
            { value: 'disease', name: this.locale.translate('disease') },
            { value: 'pest', name: this.locale.translate('pest') },
            { value: 'foliarFertilizer', name: this.locale.translate('foliarFertilizer') },
            { value: 'growthRegulation', name: this.locale.translate('growthRegulation') },
            { value: 'haulmStripping', name: this.locale.translate('haulmStripping') },
            { value: 'productTreatment', name: this.locale.translate('productTreatment') },
            { value: 'other', name: this.locale.translate('other') },
          ];
          const treatment = cloneDeep(action.treatment) as Treatment;
          treatment.treatmentType = template.templateType as unknown as TreatmentType;
          const cropAndCropAids = action.treatment.aidAdvices.map(({ cropAidId }) => ({
            cropId: template.cropId,
            cropAidId,
          }));
          return this.dialog
            .form(TreatmentDialogComponent, {
              title: this.locale.translate('treatmentDialogTitle'),
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
              cropAids:
                treatment.treatmentType === TreatmentType.GBM
                  ? cropAids.filter((cropAid) => cropAid.isCropProtection && !cropAid.isHidden)
                  : template.isSkal
                    ? organicCropAids
                    : cropAids.filter((cropAid) => cropAid.isFertilizer),
              salesOrderPeriods,
              treatment,
              disableClose: true,
              fullscreen: true,
              pushHistoryState: false,
              template,
              objectives:
                treatment.treatmentType === TreatmentType.Fertilization
                  ? fertilizationObjectives
                  : gbmObjectives,
              cropId: template.cropId,
              isPlanetProof: template.isPlanetProof,
            })
            .pipe(
              switchMap(({ status, result }) =>
                status === DIALOG_RETURN_TYPES.confirm
                  ? [new SaveTreatment(result, action.index, salesOrderPeriods)]
                  : EMPTY,
              ),
              startWith(LegalRestrictionActions.getLegalRestrictions({ cropAndCropAids })),
            );
        },
      ),
    ),
  );

  saveTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.SaveTreatment),
      withLatestFrom(this.store.pipe(select(selectEditingTemplate))),
      switchMap(([action, template]: [SaveTreatment, Template]) => {
        const treatment = cloneDeep(action.treatment);
        treatment.treatmentType = template.templateType as unknown as TreatmentType;
        return this.api.upsertTreatment(treatment, template).pipe(
          switchMap(() => [new GetEditingTemplate(template.id)]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  removeTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.RemoveTreatment),
      withLatestFrom(this.store.pipe(select(selectEditingTemplate))),
      switchMap(([action, template]: [SaveTreatment, Template]) =>
        this.api.removeTreatment(action.treatment, template).pipe(
          switchMap(() => [new GetEditingTemplate(template.id)]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveTreatmentSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.SaveTreatmentSchema),
      withLatestFrom(this.store.pipe(select(selectEditingTemplate))),
      switchMap(([action, template]: [SaveTreatmentSchema, Template]) =>
        this.api.upsertTreatmentSchema(action.treatmentSchema, template).pipe(
          switchMap(() => [new GetEditingTemplate(template.id)]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  removeTreatmentSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.RemoveTreatmentSchema),
      withLatestFrom(this.store.pipe(select(selectEditingTemplate))),
      switchMap(([action, template]: [SaveTreatmentSchema, Template]) =>
        this.api.removeTreatmentSchema(action.treatmentSchema, template).pipe(
          switchMap(() => [new GetEditingTemplate(template.id)]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addTreatmentSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.AddTreatmentSchema),
      withLatestFrom(this.store.pipe(select(areTreatmentsValid))),
      switchMap(([_, treatmentsValid]) =>
        treatmentsValid
          ? of(
              new EditTreatmentSchema({
                id: null,
                name: '',
                description: '',
                treatmentIds: [],
              }),
            )
          : this.dialog
              .notify({
                title: this.locale.translate('cannotAddSchema'),
                text: this.locale.translate('invalidTreatments'),
              })
              .pipe(switchMap(() => EMPTY)),
      ),
    ),
  );

  openTreatmentSchemaDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.EditTreatmentSchema),
      withLatestFrom(this.store.pipe(select(selectEditingTreatments))),
      switchMap(([action, treatments]: [EditTreatmentSchema, Treatment[]]) =>
        this.dialog
          .form(TreatmentSchemaDialogComponent, {
            title: this.locale.translate('treatmentSchemaDialogTitle'),
            cancelButtonText: this.locale.translate('cancel'),
            confirmButtonText: this.locale.translate('save'),
            treatmentSchema: action.treatmentSchema,
            treatments,
            disableClose: true,
            fullscreen: false,
          })
          .pipe(
            switchMap(({ status, result }) =>
              status === DIALOG_RETURN_TYPES.confirm ? [new SaveTreatmentSchema(result)] : EMPTY,
            ),
          ),
      ),
    ),
  );

  openCopyTreatmentsDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActionTypes.OpenCopyTreatmentsDialog),
      withLatestFrom(
        this.store.pipe(select(selectEditingTemplate)),
        this.store.pipe(select(selectCrops)),
        this.store.pipe(select(selectSoilTypes)),
        this.store.pipe(select(selectAvailableCropYears)),
        this.store.pipe(select(selectEditingTreatments)),
        this.store.pipe(select(selectSalesOrderPeriods)),
        this.store.pipe(select(areTreatmentsValid)),
      ),
      switchMap(
        ([
          _,
          template,
          crops,
          soilTypes,
          cropYears,
          treatments,
          salesOrderPeriods,
          treatmentsValid,
        ]: [
          never,
          TemplateTableItem,
          Crop[],
          SoilType[],
          number[],
          Treatment[],
          SalesOrderPeriod[],
          boolean,
        ]) => {
          const soilTypeIndependent: SoilType = {
            id: 0,
            name: this.locale.translate('soilTypeIndependent'),
          };
          return template.treatments.length
            ? treatmentsValid
              ? this.dialog
                  .form(CopyTreatmentsDialogComponent, {
                    title: this.locale.translate('copyStandardDialogTitle'),
                    cancelButtonText: this.locale.translate('cancel'),
                    confirmButtonText: this.locale.translate('save'),
                    crops,
                    soilTypes: [soilTypeIndependent, ...soilTypes],
                    cropYears,
                    treatments,
                    treatmentSchemas: template.treatmentSchemas,
                    salesOrderPeriods,
                    disableClose: true,
                    fullscreen: true,
                  })
                  .pipe(
                    switchMap(({ status, result }) =>
                      status === DIALOG_RETURN_TYPES.confirm
                        ? [
                            new CopyTreatments({
                              ...result,
                              soilTypeIds:
                                result.soilTypeIds.findIndex((soilTypeId) => soilTypeId === 0) > -1
                                  ? [
                                      null,
                                      ...result.soilTypeIds.filter((soilTypeId) => soilTypeId > 0),
                                    ]
                                  : result.soilTypeIds,
                              templateId: template.id,
                              templateType: template.templateType,
                              province: result.province,
                            }),
                          ]
                        : EMPTY,
                    ),
                  )
              : this.dialog
                  .notify({
                    title: this.locale.translate('cannotCopyTemplate'),
                    text: this.locale.translate('invalidTreatments'),
                  })
                  .pipe(switchMap(() => EMPTY))
            : this.dialog
                .notify({ text: this.locale.translate('copyTreatmentsEmptyText') })
                .pipe(switchMap(() => EMPTY));
        },
      ),
    ),
  );

  copyTreatments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TemplateActionTypes.CopyTreatments),
        switchMap(({ request }: CopyTreatments) =>
          this.api.copyTreatments(request).pipe(
            switchMap((result) => {
              const isDuplicatesSkipped =
                result.reasonCode === 'copy-treatments-duplicates-skipped';
              const hasWarning =
                result.numberOfTemplatesAffected === 0 || isDuplicatesSkipped;

              if (!hasWarning) return EMPTY;

              const title = isDuplicatesSkipped
                ? this.locale.translate('copyTreatmentsDuplicatesTitle')
                : this.locale.translate('cannotCopyTemplate');

              return this.dialog
                .notify({
                  title,
                  text: result.reason ?? this.locale.translate('copyTreatmentsEmptyText'),
                })
                .pipe(switchMap(() => EMPTY));
            }),
          ),
        ),
      ),
    { dispatch: false },
  );
}
